import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { TutorQuizDTO, TutorQuizOptionDTO, TutorQuizQuestionDTO } from '../../../services/tutorQuizService';

interface Props {
  quiz: TutorQuizDTO;
  onSave: () => void;
  onDiscard: () => void;
}

function questionTypeBadge(type: string) {
  if (type === 'TRUE_FALSE') return <Chip label="T/F" size="small" color="secondary" />;
  return <Chip label="MC" size="small" color="primary" />;
}

function QuestionCard({ question, index }: { question: TutorQuizQuestionDTO; index: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={1}>
        <Typography variant="subtitle2" sx={{ minWidth: 24 }}>{index + 1}.</Typography>
        {questionTypeBadge(question.questionType)}
        <Typography variant="body1" sx={{ flex: 1 }}>{question.questionText}</Typography>
      </Stack>
      <Stack spacing={0.5} ml={4}>
        {question.options.map((opt: TutorQuizOptionDTO) => (
          <Box
            key={opt.id ?? opt.displayOrder}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: opt.isCorrect ? 'success.light' : 'grey.100',
              border: opt.isCorrect ? '1.5px solid' : '1px solid',
              borderColor: opt.isCorrect ? 'success.main' : 'grey.300',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: opt.isCorrect ? 700 : 400, color: opt.isCorrect ? 'success.dark' : 'text.primary' }}
            >
              {opt.isCorrect ? '✓ ' : ''}{opt.optionText}
            </Typography>
          </Box>
        ))}
      </Stack>
      {question.explanation && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 4, display: 'block' }}>
          Explanation: {question.explanation}
        </Typography>
      )}
    </Paper>
  );
}

export default function TutorQuizPreview({ quiz, onSave, onDiscard }: Props) {
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{quiz.title}</Typography>
      <Stack direction="row" spacing={2} mb={2}>
        {quiz.timeLimitMinutes && (
          <Typography variant="caption" color="text.secondary">
            Time limit: {quiz.timeLimitMinutes} min
          </Typography>
        )}
        {quiz.minPassPercent && (
          <Typography variant="caption" color="text.secondary">
            Pass: {quiz.minPassPercent}%
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {quiz.questions?.length ?? 0} questions
        </Typography>
      </Stack>
      <Stack spacing={1.5} mb={3}>
        {quiz.questions?.map((q, i) => (
          <QuestionCard key={q.id ?? i} question={q} index={i} />
        ))}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" color="error" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving…' : 'Save Quiz'}
        </Button>
      </Stack>
    </Box>
  );
}
